import { CrudOperation, crudOperations } from 'common/type/enum';
import { singularize } from 'common/utils';
import { usePermissions } from 'hooks';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { KeycloakRole, Seletor, extractCrudRole } from 'roles';
import { ValidPath, allRoutes } from 'routes';

type Visibility = {
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showLog?: boolean;
  showNew?: boolean;
};

type Paths = {
  viewPath?: ValidPath;
  editPath?: ValidPath;
  logPath?: ValidPath;
  newPath?: ValidPath;
};

export type CrudRoutesProps = {
  visibility?: Visibility;
  paths?: Paths;
};

const pathEquals =
  (routePath: string | readonly string[]) =>
  (route: typeof allRoutes[number]) => {
    if (!('path' in route)) {
      return false;
    }
    if (Array.isArray(route.path)) {
      return route.path.includes(routePath);
    }
    return route.path === routePath;
  };

const pathContains =
  (routePath: string) => (route: typeof allRoutes[number]) => {
    if (!('path' in route)) {
      return false;
    }
    if (Array.isArray(route.path)) {
      return route.path.some(path => path.includes(routePath));
    }
    return typeof route.path === 'string' && route.path.includes(routePath);
  };

const isOperation =
  (crudOperation: CrudOperation) => (route: typeof allRoutes[number]) =>
    'role' in route && route.role?.endsWith(crudOperation);

const extractPath = (route?: typeof allRoutes[number]): string => {
  if (!route || !('path' in route)) {
    return '';
  }
  const { path } = route as { path: string | string[] };
  if (Array.isArray(path)) {
    return path.find(path => path.includes(':id')) || path[0];
  }
  return path;
};

export const useCrudRoutes = ({
  visibility: propVisibility,
  paths
}: CrudRoutesProps) => {
  const visibility = {
    showView: propVisibility?.showView ?? true,
    showEdit: propVisibility?.showEdit ?? true,
    showDelete: propVisibility?.showDelete ?? true,
    showLog: propVisibility?.showLog ?? true,
    showNew: propVisibility?.showNew ?? true
  };

  const permissions = usePermissions();

  const { pathname } = useLocation();

  const currentRoute = useMemo(() => {
    const baseRoute = allRoutes.find(pathEquals(pathname));

    if (!baseRoute) {
      throw new Error(
        `Não foi possível encontrar a rota base para o pai: ${pathname}`
      );
    }

    return baseRoute;
  }, [pathname]);

  const routes = useMemo(() => {
    const childrenRoutes = allRoutes.filter(route => {
      if (!('parent' in route)) {
        return false;
      }
      if (Array.isArray(currentRoute.path)) {
        return currentRoute.path.includes(route.parent);
      }
      return route.parent === currentRoute.path;
    });

    const viewRoute = paths?.viewPath
      ? allRoutes.find(pathEquals(paths.viewPath))
      : childrenRoutes.find(pathContains('/view'));

    const editRoute = paths?.editPath
      ? allRoutes.find(pathEquals(paths.editPath))
      : childrenRoutes.find(isOperation('update'));

    const logRoute = paths?.logPath
      ? allRoutes.find(pathEquals(paths.logPath))
      : childrenRoutes.find(isOperation('log')) ||
        childrenRoutes.find(pathContains('/history'));

    const newRoute = paths?.newPath
      ? allRoutes.find(pathEquals(paths.newPath))
      : childrenRoutes.find(isOperation('create'));

    return { viewRoute, editRoute, logRoute, newRoute };
  }, [paths]);

  const title = useMemo(
    () =>
      routes.editRoute?.title || singularize(currentRoute.title) || 'Entidade',
    [currentRoute, routes?.editRoute]
  );

  const pluralTitle = useMemo(() => currentRoute.title, [currentRoute]);

  const crudRoles = useMemo(() => {
    let defaultKeycloakRole: KeycloakRole | undefined = undefined;
    if ('role' in currentRoute && currentRoute.role) {
      defaultKeycloakRole = currentRoute.role;
    }

    let defaultSeletor: Seletor | undefined = undefined;
    if ('seletor' in currentRoute && currentRoute.seletor) {
      defaultSeletor = currentRoute.seletor;
    }
    if (!defaultSeletor) {
      throw new Error(
        `Não foi possível extrair o seletor da rota: ${currentRoute.path}`
      );
    }

    const crudRole = extractCrudRole(defaultKeycloakRole);
    if (!crudRole) {
      throw new Error(
        `Não foi possível extrair a role CRUD da rota: ${currentRoute.path}`
      );
    }

    const usedViewRole: KeycloakRole = `${crudRole}_${crudOperations.read}`;
    const usedEditRole: KeycloakRole = `${crudRole}_${crudOperations.update}`;
    const usedDeleteRole: KeycloakRole = `${crudRole}_${crudOperations.delete}`;
    const usedLogRole: KeycloakRole = `${crudRole}_${crudOperations.log}`;
    const usedNewRole: KeycloakRole = `${crudRole}_${crudOperations.create}`;

    const canView =
      visibility.showView &&
      permissions.enableView(usedViewRole, defaultSeletor);
    const canEdit =
      visibility.showEdit &&
      permissions.enableEdit(usedEditRole, defaultSeletor);
    const canLog =
      visibility.showLog &&
      permissions.enableHistory(usedLogRole, defaultSeletor);
    const canNew =
      visibility.showNew && permissions.enableNew(usedNewRole, defaultSeletor);
    const canDelete =
      visibility.showDelete &&
      permissions.enableDelete(usedDeleteRole, defaultSeletor);

    return { canView, canEdit, canLog, canNew, canDelete };
  }, [currentRoute, visibility, permissions]);

  return useMemo(() => {
    const viewPath = crudRoles.canView && extractPath(routes.viewRoute);
    const editPath = crudRoles.canEdit && extractPath(routes.editRoute);
    const logPath = crudRoles.canLog && extractPath(routes.logRoute);
    const newPath =
      crudRoles.canNew &&
      (extractPath(routes.newRoute) || editPath?.replace(':id', 'new'));

    return {
      viewPath,
      editPath,
      logPath,
      newPath,
      canDelete: crudRoles.canDelete,
      title,
      pluralTitle
    };
  }, [crudRoles, routes]);
};
